import React, { useContext } from 'react';

import { useMapStateStore, useConfigStore, useMapInteractionStore } from '../../stores';
import SiteSeeContext from './SiteSeeContext';

import SiteSeeNavigationGuide from './SiteSeeNavigationGuide';
import SiteSeeControlsGuide from './SiteSeeControlsGuide';
import SiteSeeZoomInGuide from './SiteSeeZoomInGuide';
import SiteSeeZoomOutGuide from './SiteSeeZoomOutGuide';
import SiteSeeZoomZonesGuide from './SiteSeeZoomZonesGuide';
import SiteSeeFiltersGuide from './SiteSeeFiltersGuide';
import SiteSeeLevelsGuide from './SiteSeeLevelsGuide';
import SiteSeeAmenitiesGuide from './SiteSeeAmenitiesGuide';
import SiteSeeResidencesGuide from './SiteSeeResidencesGuide';
import SiteSeeMarkersGuide from './SiteSeeMarkersGuide';

/**
 * GUIDE_META - Single source of truth for guide metadata.
 * Contains static properties: id, indices, pulse behavior, title, and styling.
 * Dynamic properties (components, buttons, tourFunc) are added in siteSeeGuideData.
 */
export const GUIDE_META = {
  intro: {
    id: 'intro', desktopIndex: null, mobileIndex: null, pulseTarget: null,
  },
  warning: {
    id: 'warning', desktopIndex: null, mobileIndex: null, pulseTarget: null,
  },
  navigation: {
    id: 'navigation', desktopIndex: 1, mobileIndex: 1, pulseTarget: null, title: 'Moving Around the Community',
  },
  controls: {
    id: 'controls', desktopIndex: 2, mobileIndex: null, pulseTarget: 'controls', title: 'Change Community View', styling: { arrow: 'down', align: ['bottom', 'right'] },
  },
  zoomZones: {
    id: 'zoomZones', desktopIndex: 3, mobileIndex: 2, pulseTarget: 'zoomZones', title: 'Explore Community Areas',
  },
  amenities: {
    id: 'amenities', desktopIndex: 4, mobileIndex: 3, pulseTarget: 'amenities', title: 'Explore Community Amenities',
  },
  residences: {
    id: 'residences', desktopIndex: 5, mobileIndex: 4, pulseTarget: 'residences', title: 'Explore Community Residences',
  },
  zoomIn: {
    id: 'zoomIn', desktopIndex: null, mobileIndex: 5, pulseTarget: 'zoomIn', title: 'Zoom In to Community',
  },
  zoomOut: {
    id: 'zoomOut', desktopIndex: null, mobileIndex: 6, pulseTarget: 'zoomOut', title: 'Zoom Out of Community',
  },
  levels: {
    id: 'levels', desktopIndex: 7, mobileIndex: 7, pulseTarget: 'levels', title: 'Explore Community Levels', styling: { arrow: 'down', align: ['bottom', 'left'] },
  },
  filters: {
    id: 'filters', desktopIndex: 6, mobileIndex: 8, pulseTarget: 'filters', title: 'Filter Community Residences', styling: { arrow: 'up', align: ['top', 'left'] },
  },
  markers: {
    id: 'markers', desktopIndex: null, mobileIndex: 9, pulseTarget: 'markers', title: 'Show/Hide Community Amenity Markers',
  },
};

// Pulse targets - derived from GUIDE_META for easy import by components
export const PULSE_TARGETS = {
  CONTROLS: GUIDE_META.controls.pulseTarget,
  ZOOM_ZONES: GUIDE_META.zoomZones.pulseTarget,
  AMENITIES: GUIDE_META.amenities.pulseTarget,
  RESIDENCES: GUIDE_META.residences.pulseTarget,
  FILTERS: GUIDE_META.filters.pulseTarget,
  LEVELS: GUIDE_META.levels.pulseTarget,
  ZOOM_IN: GUIDE_META.zoomIn.pulseTarget,
  ZOOM_OUT: GUIDE_META.zoomOut.pulseTarget,
  MARKERS: GUIDE_META.markers.pulseTarget,
};

// Build index-to-pulseTarget maps from GUIDE_META
const buildPulseMaps = () => {
  const desktop = {};
  const mobile = {};
  Object.values(GUIDE_META).forEach((guide) => {
    if (guide.pulseTarget) {
      if (guide.desktopIndex !== null) desktop[guide.desktopIndex] = guide.pulseTarget;
      if (guide.mobileIndex !== null) mobile[guide.mobileIndex] = guide.pulseTarget;
    }
  });
  return { desktop, mobile };
};

const PULSE_MAPS = buildPulseMaps();

/**
 * Get the pulse target for a given guide index.
 * @param {number} guideIndex - The current siteSeeGuideId
 * @param {boolean} isMobile - Whether the user is on mobile
 * @returns {string|null} - The pulse target (guide id) or null if none
 */
export const getPulseTarget = (guideIndex, isMobile) => {
  const map = isMobile ? PULSE_MAPS.mobile : PULSE_MAPS.desktop;
  return map[guideIndex] || null;
};

const siteSeeGuideData = (setTrigger, onNextTipClick, handleModalHide) => {
  const {
    setShowSiteSeeGuide,
    siteSeeGuideMode,
    siteSeeName,
    isMobile,
  } = useContext(SiteSeeContext);

  // Zustand stores
  const setActiveZoom = useMapStateStore((state) => state.setActiveZoom);
  const setResetMap = useMapStateStore((state) => state.setResetMap);
  const setActiveMapType = useMapStateStore((state) => state.setActiveMapType);
  const setPOIVisible = useMapStateStore((state) => state.setPOIVisible);
  const setActiveLevelSvgId = useMapStateStore((state) => state.setActiveLevelSvgId);
  const initialLevelSvgId = useMapStateStore((state) => state.initialLevelSvgId);
  const config = useConfigStore((state) => state.config);
  const mapConfig = useConfigStore((state) => state.mapConfig);
  const featureFlags = useConfigStore((state) => state.featureFlags);

  const { siteSeeIntro } = config || {};
  const { googleMapsConfig } = mapConfig || {};
  const { enableLevels: mapZoomToEnableLevels } = googleMapsConfig?.zooms || {};
  const { platformShowMapPlaceLabelsInitially } = featureFlags || {};

  // Map interaction store
  const setMarkersVisible = useMapInteractionStore((state) => state.setMarkersVisible);

  const createButtons = () => {
    const buttons = [];
    if (siteSeeGuideMode === 'tour' && isMobile) {
      buttons.push({
        key: 'nextTip',
        text: `Next ${siteSeeName} Tip`,
        className: 'modal__btn modal__btn--inline',
        func: () => {
          onNextTipClick();
          // Remaing funcs will be called in onExitFinishFunc after transition.
        },
      });
    }

    buttons.push({
      key: 'close',
      text: siteSeeGuideMode === 'tour' ? `Go to ${siteSeeName}` : 'Close Tip',
      className: 'modal__btn--minor modal__btn--inline',
      func: () => {
        handleModalHide();
        // Remaing funcs will be called in onExitFinishFunc after transition.
      },
    });

    return buttons;
  };

  const buttons = createButtons();

  const finalTipCloseBtn = {
    key: 'endTour',
    text: `Go to ${siteSeeName}`,
    className: 'modal__btn',
    func: () => {
      setTrigger('endTour');
      setShowSiteSeeGuide(false);
      // Remaing funcs will be called in onExitFinishFunc after transition.
    },
  };

  const resetMap = () => {
    setResetMap(true);
    setActiveMapType('roadmap');
    setActiveZoom(mapZoomToEnableLevels - 1);
    setPOIVisible(false);
    setMarkersVisible(true);
  };

  const resetMapEnabledLevelsShowMarkers = () => {
    setActiveMapType('roadmap');
    setActiveZoom(mapZoomToEnableLevels);
    setActiveLevelSvgId(initialLevelSvgId);
    setPOIVisible(false);
    setMarkersVisible(true);
  };

  const resetMapEnabledLevels = () => {
    setActiveMapType('roadmap');
    setActiveZoom(mapZoomToEnableLevels);
    setActiveLevelSvgId(initialLevelSvgId);
    setPOIVisible(false);
    setMarkersVisible(platformShowMapPlaceLabelsInitially);
  };

  return ([{
    ...GUIDE_META.intro,
    title: `Welcome to Our ${siteSeeName}!`,
    text: siteSeeIntro,
    modalBlur: true,
    buttons: [{
      key: 'startTour',
      text: `See ${siteSeeName} Tips`,
      className: 'modal__btn modal__btn--inline',
      func: () => {
        setTrigger('startTour');
        setShowSiteSeeGuide(false);
      },
    },
    {
      key: 'endTour',
      text: `Skip Tips & Go to ${siteSeeName}`,
      className: 'modal__btn--minor modal__btn--inline',
      func: () => {
        setTrigger('endTour');
        setShowSiteSeeGuide(false);
      },
    }],
  },
  {
    ...GUIDE_META.warning,
    title: `Leave ${siteSeeName} Tips?`,
    text: `Are you sure you want to leave the ${siteSeeName} tips?`,
    modalBlur: true,
    buttons: [{
      key: 'endTour',
      text: `Yes, Go to ${siteSeeName}`,
      className: 'modal__btn modal__btn--inline',
      func: () => {
        setTrigger('endTour');
        setShowSiteSeeGuide(false);
        resetMap();
      },
    },
    {
      key: 'cancelEndTour',
      text: `No, Return to ${siteSeeName} Tips`,
      className: 'modal__btn--minor modal__btn--inline',
      func: () => {
        setTrigger('cancelEndTour');
        setShowSiteSeeGuide(false);
      },
    }],
  },
  {
    ...GUIDE_META.navigation,
    component: <SiteSeeNavigationGuide />,
    modalBlur: false,
    buttons,
  },
  {
    ...GUIDE_META.controls,
    modalBlur: false,
    component: <SiteSeeControlsGuide />,
    buttons,
  },
  {
    ...GUIDE_META.zoomZones,
    modalBlur: false,
    component: <SiteSeeZoomZonesGuide />,
    tourFunc: () => resetMap(),
    buttons,
  },
  {
    ...GUIDE_META.amenities,
    modalBlur: false,
    component: <SiteSeeAmenitiesGuide />,
    target: null,
    tourFunc: () => resetMapEnabledLevelsShowMarkers(),
    buttons,
  },
  {
    ...GUIDE_META.residences,
    component: <SiteSeeResidencesGuide />,
    modalBlur: false,
    target: null,
    tourFunc: () => resetMapEnabledLevels(),
    buttons,
  },
  {
    ...GUIDE_META.zoomIn,
    modalBlur: false,
    component: <SiteSeeZoomInGuide />,
    tourFunc: () => resetMapEnabledLevels(),
    buttons,
  },
  {
    ...GUIDE_META.zoomOut,
    modalBlur: false,
    component: <SiteSeeZoomOutGuide />,
    tourFunc: () => resetMapEnabledLevels(),
    buttons,
  },
  {
    ...GUIDE_META.filters,
    modalBlur: false,
    component: <SiteSeeFiltersGuide />,
    tourFunc: () => resetMapEnabledLevels(),
    buttons,
  },
  {
    ...GUIDE_META.levels,
    modalBlur: false,
    component: <SiteSeeLevelsGuide />,
    tourFunc: () => resetMapEnabledLevels(),
    buttons: !isMobile && siteSeeGuideMode === 'tour' ? [finalTipCloseBtn] : buttons,
  },
  {
    ...GUIDE_META.markers,
    modalBlur: false,
    component: <SiteSeeMarkersGuide />,
    tourFunc: () => resetMapEnabledLevels(),
    buttons: !isMobile ? buttons : [finalTipCloseBtn],
  }]);
};

export default siteSeeGuideData;
